import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowRight, FlaskConical, Database, Cog, BarChart3, Search, GitBranch, TrendingUp } from "lucide-react";
import { siteConfig } from "../config";
import { useView } from "../context/ViewContext";
import { FadeIn } from "./common";
import SciBackground from "./SciBackground";

const FLOW = [
  { icon: FlaskConical, label: "Manufacturing Process", detail: "Regulated production and Product Quality operations generate outcomes, measurements and failure signals." },
  { icon: Database, label: "Data Acquisition", detail: "Run results, analytical files, APIs and sensors are collected into trustworthy, traceable datasets." },
  { icon: Cog, label: "Automation", detail: "Manual retrieval, cleaning and reconciliation become scheduled, repeatable digital workflows." },
  { icon: BarChart3, label: "Product Quality Analytics", detail: "FPY, Pareto, distributions and trends make losses measurable and comparable." },
  { icon: Search, label: "Root Cause", detail: "Scientific RCA converts process signatures into tested, verified causal understanding." },
  { icon: GitBranch, label: "CAPA / Controlled Change", detail: "Evidence becomes controlled change — ECO, SOP/WI revision, training — through the QMS." },
  { icon: TrendingUp, label: "Sustained Improvement", detail: "Effectiveness monitoring confirms the gain is real and stays controlled." },
];

const CAPABILITIES = ["Product Quality", "QMS", "GMP", "RCA", "CAPA", "Python", "REST APIs", "IoT", "Statistical Analysis", "Data Pipelines", "Manufacturing Analytics", "Continuous Improvement"];

function ProcessFlow() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(4);
  return (
    <div className="panel p-5 sci-grid" data-testid="hero-process-flow">
      <p className="overline-label mb-4">Molecule / Process → Measurement → Decision</p>
      <div className="space-y-1">
        {FLOW.map((node, i) => {
          const Icon = node.icon;
          const isActive = active === i;
          return (
            <div key={node.label}>
              <motion.button
                data-testid={`hero-flow-node-${i}`}
                onClick={() => setActive(i)}
                initial={reduce ? false : { opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.12 * i, duration: 0.4 }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm border text-left transition-colors duration-200 ${
                  isActive ? "border-cyan bg-cyan/10" : "border-transparent hover:border-teal/40"
                }`}
              >
                <span className={`font-mono text-[10px] w-5 ${isActive ? "text-cyan" : "text-steel"}`}>{String(i + 1).padStart(2, "0")}</span>
                <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-cyan" : "text-teal"}`} aria-hidden="true" />
                <span className={`text-sm font-body ${isActive ? "text-paper" : "text-pale/70"}`}>{node.label}</span>
                {isActive && !reduce && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-cyan animate-pulse-soft" aria-hidden="true" />}
              </motion.button>
              {i < FLOW.length - 1 && (
                <div className="ml-[26px] h-3 w-px bg-teal/40" aria-hidden="true" />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-4 border-t border-cyan/10 min-h-[56px]" data-testid="hero-flow-detail">
        <p className="text-xs text-pale/80 leading-relaxed">{FLOW[active].detail}</p>
      </div>
    </div>
  );
}

export default function Hero() {
  const { goTo, switchMode } = useView();
  const heroRef = useRef(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    if (!window.matchMedia("(pointer: fine)").matches) return undefined;
    const onMove = (e) => {
      const nx = (e.clientX / window.innerWidth) * 2 - 1;
      const ny = (e.clientY / window.innerHeight) * 2 - 1;
      el.style.setProperty("--px", nx.toFixed(3));
      el.style.setProperty("--py", ny.toFixed(3));
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
    };
  }, []);

  return (
    <section id="overview" ref={heroRef} className="relative sci-grid scroll-mt-16 overflow-hidden" data-testid="hero-section">
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(7,17,31,0) 0%, rgba(11,39,48,0.55) 100%), radial-gradient(56% 46% at 82% 0%, rgba(32,184,205,0.15), transparent 62%), radial-gradient(46% 46% at 8% 100%, rgba(19,124,139,0.17), transparent 62%)" }} />
      <SciBackground variant="hero" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-24 lg:pt-32 lg:pb-36">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <FadeIn>
              <div className="flex items-center gap-3">
                <span aria-hidden="true" className="h-[3px] w-12 rounded-full bg-gradient-to-r from-cyan to-teal" />
                <p className="font-sans font-semibold text-xl sm:text-2xl tracking-wide text-paper" data-testid="hero-overline">
                  Poojan Patel
                </p>
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-cyan/80 mt-3 sm:ml-[60px]">{siteConfig.brand}</p>
            </FadeIn>
            <FadeIn delay={0.08}>
              <h1 className="font-sans tracking-tight text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-paper mt-7" data-testid="hero-headline">
                {siteConfig.headline}
              </h1>
            </FadeIn>
            <FadeIn delay={0.16}>
              <p className="font-sans font-medium text-lg md:text-xl text-cyan mt-6 leading-snug max-w-xl" data-testid="hero-supporting-line">
                Streamlining Manufacturing Data, Product Quality Systems &amp; Digital Workflows
              </p>
            </FadeIn>
            <FadeIn delay={0.22}>
              <p className="text-pale/85 text-base md:text-lg mt-5 leading-relaxed max-w-xl" data-testid="hero-subheadline">
                I combine regulated manufacturing, Product Quality, Quality Systems, scientific RCA and digital automation to convert complex
                manufacturing evidence into faster investigations, controlled process improvements and scalable decision-support systems.
              </p>
            </FadeIn>
            <FadeIn delay={0.28}>
              <p className="font-mono text-xs text-steel mt-5 tracking-wide">
                Product Quality Systems • Digital Manufacturing • Hypothesis-Driven RCA • QMS • Python / REST APIs • Manufacturing Intelligence •
                IoT / Time-Series Data • Operational Excellence
              </p>
            </FadeIn>
            <FadeIn delay={0.34}>
              <div className="flex flex-wrap gap-3.5 mt-9">
                <button
                  data-testid="hero-explore-work-button"
                  onClick={() => goTo("projects")}
                  className="group flex items-center gap-2 bg-cyan text-navy font-sans font-semibold text-sm px-6 py-3.5 rounded-sm shadow-lg shadow-cyan/20 hover:bg-pale hover:shadow-cyan/10 transition-all"
                >
                  Explore My Work
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </button>
                <button
                  data-testid="hero-analytics-lab-button"
                  onClick={() => goTo("analytics-lab")}
                  className="flex items-center gap-2 border border-cyan/50 text-cyan font-sans text-sm px-6 py-3.5 rounded-sm hover:bg-cyan/10 transition-colors"
                >
                  Open Analytics Lab
                </button>
                <button
                  data-testid="hero-recruiter-view-button"
                  onClick={() => switchMode("recruiter")}
                  className="flex items-center gap-2 border border-teal/40 text-pale/80 font-sans text-sm px-6 py-3.5 rounded-sm hover:border-cyan/60 hover:text-paper transition-colors"
                >
                  60-Second Recruiter View
                </button>
              </div>
            </FadeIn>
            <FadeIn delay={0.38}>
              <div className="flex flex-wrap gap-1.5 mt-10" data-testid="hero-capability-strip">
                {CAPABILITIES.map((c) => (
                  <span key={c} className="tech-tag">
                    {c}
                  </span>
                ))}
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={0.2}>
            <ProcessFlow />
          </FadeIn>
        </div>
        <div className="hidden lg:flex justify-center mt-16" aria-hidden="true">
          <ArrowDown className="w-4 h-4 text-teal/60 animate-pulse-soft" />
        </div>
      </div>
    </section>
  );
}
